<template>
  <button
    :type="type"
    :disabled="disabled"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="ariaLabel"
    :class="[BASE, VARIANTI[variant], soloIcona ? DIMENSIONI_ICONA[size] : DIMENSIONI[size], fullWidth && 'w-full', compattoSuTelefono && 'max-sm:w-8 max-sm:px-0']"
    @click="onClick"
  >
    <svg
      v-if="loading"
      class="size-[1em] shrink-0 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      data-test="ui-button-spinner"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-opacity="0.25" stroke-width="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
    </svg>
    <span
      v-else-if="icon"
      class="inline-flex size-[1em] shrink-0 items-center justify-center leading-none"
      aria-hidden="true"
      data-test="ui-button-icon"
    >
      <i :class="[icon, 'block leading-none', ANIMAZIONI_ICONA[animazioneIcona]]" />
    </span>
    <span v-if="label || $slots.default" class="leading-none" :class="compattoSuTelefono && 'max-sm:sr-only'"><slot>{{ label }}</slot></span>
  </button>
</template>

<script setup lang="ts">
// Bottone del design system (design-system.md, sezioni 4 e 7). Elemento <button> nativo: tastiera, focus e
// disabled arrivano dal browser; feedback (hover, active, focus-visible) dalla utility `interactive`.
// Sostituisce nel tempo btn-terracotta (primary), btn-graphite (secondary) e btn-outline (ghost).
type Variante = "primary" | "secondary" | "ghost" | "danger" | "contrast" | "link" | "soft" | "steel";
type Dimensione = "sm" | "md";
type AnimazioneIcona = "none" | "nudge" | "pop" | "rotate";

const props = withDefaults(
  defineProps<{
    variant?: Variante;
    size?: Dimensione;
    label?: string;
    // classe dell'icona FontAwesome, es. "fal fa-check"; sta sempre a sinistra del testo
    icon?: string;
    // animazione dell'icona in hover/focus da tastiera. Se omessa è automatica: `nudge` per le frecce e l'invio, `pop` per le altre
    iconHover?: AnimazioneIcona;
    loading?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    type?: "button" | "submit" | "reset";
    // obbligatorio per i bottoni con sola icona
    ariaLabel?: string;
    // sotto `sm` il testo resta solo per gli screen reader e il bottone diventa un quadrato con la sola icona (serve `icon`): per le barre con tante azioni su telefono
    soloIconaSuTelefono?: boolean;
  }>(),
  {
    variant: "primary",
    size: "md",
    loading: false,
    disabled: false,
    fullWidth: false,
    type: "button",
  },
);

const emit = defineEmits<{ click: [evento: MouseEvent] }>();
const slots = useSlots();

const ICONE_DIREZIONALI = /fa-(arrow-right|arrow-left|paper-plane|right-to-bracket|right-from-bracket|chevron-right|angle-right)\b/;
const animazioneIcona = computed<AnimazioneIcona>(
  () => props.iconHover ?? (props.icon && ICONE_DIREZIONALI.test(props.icon) ? "nudge" : "pop"),
);

const soloIcona = computed(() => !props.label && !slots.default && !!props.icon);
const compattoSuTelefono = computed(() => !!props.soloIconaSuTelefono && !!props.icon && !soloIcona.value);

if (import.meta.dev && nomeAccessibileMancante({ label: props.label, ariaLabel: props.ariaLabel, haSlot: !!slots.default })) {
  console.warn("[UiButton] manca il nome accessibile: passa `label`, uno slot o `aria-label`.");
}

// Durante il caricamento il bottone resta focalizzabile ma non ripete l'azione.
function onClick(evento: MouseEvent): void {
  if (props.disabled || props.loading) return;
  emit("click", evento);
}

const BASE =
  "interactive inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-lg border font-medium " +
  "whitespace-nowrap cursor-pointer max-lg:min-h-11 max-lg:min-w-11";

const VARIANTI: Record<Variante, string> = {
  primary:
    "border-transparent bg-accent text-on-accent enabled:not-aria-busy:hover:bg-accent-hover",
  secondary:
    "border-transparent bg-graphite text-ivory-2 enabled:not-aria-busy:hover:bg-graphite-600 dark:border-border dark:bg-surface-raised dark:enabled:not-aria-busy:hover:bg-graphite-600",
  ghost:
    "border-border-strong bg-transparent text-text-primary enabled:not-aria-busy:hover:bg-glass-strong",
  danger: "border-transparent bg-error-500 text-ivory-2 enabled:not-aria-busy:hover:bg-error-600",
  contrast:
    "border-transparent bg-text-primary text-background enabled:not-aria-busy:hover:opacity-90",
  // Azioni di secondo piano ma da vedere (modifica, rimuovi): sfondo velato e testo colorato, mai pieno come `primary`/`danger`
  soft: "border-accent/30 bg-accent/10 text-text-accent enabled:not-aria-busy:hover:border-accent/60 enabled:not-aria-busy:hover:bg-accent/20",
  // Variante «acciaio» (cool-steel): azione di secondo piano che non deve competere col terracotta (es. «Invia prova», «Modifica»)
  steel:
    "border-cool-steel/50 bg-cool-steel/15 text-text-steel enabled:not-aria-busy:hover:border-cool-steel enabled:not-aria-busy:hover:bg-cool-steel/30",
  // Azione secondaria in linea con il testo (es. «Password dimenticata?»): senza sfondo, sottolineata
  link: "h-auto! min-h-0 border-transparent bg-transparent px-1! py-1 text-text-secondary underline underline-offset-4 enabled:not-aria-busy:hover:text-accent",
};

const DIMENSIONI: Record<Dimensione, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
};

const DIMENSIONI_ICONA: Record<Dimensione, string> = {
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
};

const ANIMAZIONI_ICONA: Record<AnimazioneIcona, string> = {
  none: "",
  nudge: "icon-hover-nudge",
  pop: "icon-hover-pop",
  rotate: "icon-hover-rotate",
};
</script>
